import { Component, OnInit, ViewChild } from '@angular/core';
import { BedrockService } from '../services/bedrock.service';
import { S3Service } from '../services/s3.service';
import { Subscription } from 'rxjs';
import { Log } from '../models/prompt-log.interface';
import { MatDialog } from '@angular/material/dialog';
import { AlertModalComponent } from '../alert-modal/alert-modal.component';
import { Model, modelList } from '../shared/llms';
import { MermaidAPI } from 'ngx-markdown';
import { Clipboard } from '@angular/cdk/clipboard';

@Component({
  selector: 'app-playground',
  templateUrl: './playground.component.html',
  styleUrls: ['./playground.component.scss']
})
export class PlaygroundComponent implements OnInit {
  prompt = '';
  modelResponse = '';
  fullResponse = '';
  promptSubscription?: Subscription;
  promptInProgress = false;
  promptFinished = false;
  promptTemplates: Log[] = [];
  logsLoading = true;
  promptFilename = '';
  typingIndex = 0; // Index for typewriter effect
  typingTimeout?: NodeJS.Timeout; // Timeout ID for typing effect
  promptLoading = false;
  models = modelList;
  selectedModel: Model | null = this.models.modelList.find(model => model.id === 'claude-4-sonnet') || null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('markdownDisplay', { static: false }) markdownDisplay!: any;

  constructor(
    private bedrockService: BedrockService,
    public dialog: MatDialog,
    private s3Service: S3Service,
    private clipboard: Clipboard
  ) {}

  ngOnInit() {
    this.modelResponse = '';
    this.getLogs();
  }

  runPrompt() {
    const modelId = this.selectedModel ? this.selectedModel.id : '';
    this.clearPromptResult();
    this.promptInProgress = true;

    if (this.promptSubscription) {
      this.promptSubscription.unsubscribe();
    }

    this.promptSubscription = this.bedrockService.invokeBedrockModel(this.prompt, modelId).subscribe({
      // TODO: Add Type
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next: (response: any) => {
        this.promptFilename = response.filename;
        this.promptLoading = true;
        this.pollPromptResponse();
      },
      error: () => {
        this.openAlert('Failed', 'Could not run prompt.');
        this.promptInProgress = false;
      }
    });
  }

  pollPromptResponse() {
    const maxAttempts = 30; // Increase this if more time is needed
    const delay = 2000; // Poll every 2 seconds
    let attempts = 0;

    const poll = () => {
      attempts++;

      if (attempts > maxAttempts) {
        this.openAlert('Failed', 'Response timed out.');
        this.promptInProgress = false;
        return;
      }

      this.s3Service.pollResult(this.promptFilename).subscribe({
        // TODO: Add Type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (response: any) => {
          if (response.status === 200) {
            if (response.body.complete === 'true') {
              this.fullResponse = response.body.response;
              this.typingIndex = this.modelResponse.length; // Start typing from the current length
              this.startTypingEffect();
              this.promptInProgress = false;
            } else {
              this.fullResponse = response.body.response;
              if (this.promptLoading && this.fullResponse.length) {
                this.promptLoading = false;
              }
              this.typingIndex = this.modelResponse.length; // Continue typing from the current length
              this.startTypingEffect();
              setTimeout(poll, delay); // Retry after a delay
            }
          } else {
            setTimeout(poll, delay); // Retry after a delay
          }
        },
        error: () => {
          this.openAlert('Failed', 'Error while polling.');
          this.promptInProgress = false;
        }
      });
    };

    poll();
  }

  startTypingEffect() {
    const typingSpeed = 4; // Adjust typing speed (milliseconds per character)

    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout); // Clear any existing timeout to prevent overlapping typing
    }

    const typeCharacter = () => {
      if (this.typingIndex < this.fullResponse.length) {
        this.modelResponse += this.fullResponse.charAt(this.typingIndex);
        this.typingIndex++;
        this.typingTimeout = setTimeout(typeCharacter, typingSpeed);
      } else if (!this.promptInProgress) {
        setTimeout(() => (this.promptFinished = true), typingSpeed * 2);
      }
    };

    typeCharacter();
  }

  cancelPrompt() {
    this.promptInProgress = false;
    this.promptFinished = true;
  }

  copyToClipboard(): void {
    const html = this.markdownDisplay.element.nativeElement.innerHTML ?? '';
    const blob = new Blob([html], { type: 'text/html' });
    const data = [new ClipboardItem({ 'text/html': blob })];

    navigator.clipboard.write(data).then(
      () => {
        console.log('Rich text copied to clipboard successfully!');
      },
      err => {
        console.error('Failed to copy rich text: ', err);
      }
    );
  }

  clearPromptResult() {
    this.modelResponse = '';
    this.fullResponse = '';
    this.promptFinished = false;
    this.typingIndex = 0;
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  }

  openAlert(title: string, message: string): void {
    this.dialog.open(AlertModalComponent, {
      data: {
        title,
        message
      }
    });
  }

  mermaidChartingConfig: MermaidAPI.Config = {
    fontFamily: '"Slalom Sans", "Roboto", sans-serif',
    logLevel: MermaidAPI.LogLevel.Error,
    theme: MermaidAPI.Theme.Dark
  };

  setTemplate(option: string): void {
    this.prompt = option;
  }

  selectModel(model: Model) {
    this.selectedModel = model;
  }

  getLogs() {
    this.s3Service.getFiles('playground', 'default').subscribe({
      next: (data: Log[]) => {
        this.promptTemplates = data;
      },
      error: error => {
        console.error('There was an error while fetching the logs: ', error);
      },
      complete: () => {
        this.logsLoading = false;
      }
    });
  }
}
